import { Checkbox, Col, Collapse, Empty, Input, Radio, Row, Space, Typography } from 'antd';
import Card from 'antd/lib/card/Card';
import Spin from 'antd/lib/spin';
import moment from 'moment';
import { Helmet } from 'react-helmet-async';
import { useIsFetching, useQuery } from 'react-query';

import useAPIClient from '../api/useAPIClient';
import useAPIQuery from '../api/useAPIQuery';
import ErrorResult from '../components/ErrorResult';
import useTypedParams, { Optional } from '../hooks/useTypedParams';
import {
  CandidateDetail,
  CandidateId,
  JobPostingId,
  SurveyListObject,
  SurveyOptions,
  SurveyQuestion,
  SurveyResponseList,
} from '../types';
import styles from './CandidateCustomTests.module.css';

interface QuestionProps {
  surveyQuestion: SurveyQuestion;
}

interface RadioQuestionWithAnswerProps extends QuestionProps {
  answer?: string;
}

const RadioQuestionWithAnswer = ({ surveyQuestion, answer }: RadioQuestionWithAnswerProps) => {
  return (
    <Radio.Group value={answer}>
      <Space direction="vertical">
        {surveyQuestion.options.map((option) => (
          <Radio value={option.value}>{option.title.English}</Radio>
        ))}
      </Space>
    </Radio.Group>
  );
};

interface CheckboxQuestionWithAnswerProps extends QuestionProps {
  options?: SurveyOptions;
}

const CheckboxQuestionWithAnswer = ({
  surveyQuestion,
  options = {},
}: CheckboxQuestionWithAnswerProps) => {
  return (
    <Checkbox.Group value={Object.values(options).map((op) => op.answer)}>
      <Space direction="vertical">
        {surveyQuestion.options.map((option) => (
          <Checkbox value={option.value}>{option.title.English}</Checkbox>
        ))}
      </Space>
    </Checkbox.Group>
  );
};

interface TextQuestionWithAnswerProps {
  answer?: string;
}

const TextboxQuestionWithAnswer = ({ answer }: TextQuestionWithAnswerProps) => {
  return <Input.TextArea readOnly defaultValue={answer} />;
};

const EssayQuestionWithAnswer = ({ answer }: TextQuestionWithAnswerProps) => {
  return <Input.TextArea readOnly defaultValue={answer} />;
};

interface QuizScoreProps {
  surveyResponse: SurveyResponseList;
}

const QuizScore = ({ surveyResponse }: QuizScoreProps) => {
  const quizScore = Object.values(surveyResponse?.survey_data || {}).find(
    (surveyData) => surveyData.type === 'QUIZSCORE'
  );

  return quizScore?.shown && surveyResponse.status === 'Complete' ? (
    <>
      Resultado: &nbsp;
      <span className={styles.quizScore}>{quizScore?.answer}</span>
    </>
  ) : null;
};

const CompletedSurveyTime = ({ surveyResponse }: { surveyResponse: SurveyResponseList }) => {
  const timeFinalized = surveyResponse.date_submitted
    .replace('EDT', '-04:00')
    .replace('EST', '-05:00');

  return (
    <>
      <strong>Finalizado:&nbsp;</strong>
      {moment(timeFinalized, 'YYYY-MM-DD HH:mm:ssZ').format('LLL')}
    </>
  );
};

interface SurveyHeaderProps {
  survey: SurveyListObject;
  surveyResponse: SurveyResponseList;
}

const SurveyHeader = ({ survey, surveyResponse }: SurveyHeaderProps) => {
  return (
    <Col>
      <Row>
        <Typography.Title level={4}>{survey.title}</Typography.Title>
      </Row>
      {surveyResponse.status === 'Complete' ? (
        <Row>
          <CompletedSurveyTime surveyResponse={surveyResponse} />
        </Row>
      ) : (
        <Row>
          <strong>Em andamento</strong>
        </Row>
      )}
    </Col>
  );
};

interface SurveyCardProps {
  survey: SurveyListObject;
  candidateUUID: CandidateDetail['uuid'];
  surveyQuestion: SurveyQuestion;
}

const SurveyCard = ({ survey, candidateUUID, surveyQuestion }: SurveyCardProps) => {
  const surveyResponse = useAPIQuery(
    [
      `/alchemer/survey/${survey.id}/surveyresponse/`,
      {
        params: {
          'filter[field][1]': '[url("sguid")]',
          'filter[operator][1]': '=',
          'filter[value][1]': String(candidateUUID),
        },
      },
    ],
    {
      staleTime: Infinity,
    }
  );

  if (surveyResponse.data && surveyResponse.data?.data.length > 0) {
    return (
      <Card
        title={
          <div
            dangerouslySetInnerHTML={{
              __html: `<strong>${surveyQuestion.title.English}</strong>`,
            }}
          />
        }
        className={[styles.cardTitle, styles.imageTitle].join(' ')}
      >
        {surveyQuestion.type === 'RADIO' && (
          <RadioQuestionWithAnswer
            surveyQuestion={surveyQuestion}
            answer={surveyResponse.data?.data[0].survey_data[surveyQuestion.id]?.answer}
          />
        )}
        {surveyQuestion.type === 'CHECKBOX' && (
          <CheckboxQuestionWithAnswer
            surveyQuestion={surveyQuestion}
            options={surveyResponse.data?.data[0].survey_data[surveyQuestion.id]?.options}
          />
        )}
        {surveyQuestion.type === 'TEXTBOX' && (
          <TextboxQuestionWithAnswer
            answer={surveyResponse.data?.data[0].survey_data[surveyQuestion.id]?.answer}
          />
        )}
        {surveyQuestion.type === 'ESSAY' && (
          <EssayQuestionWithAnswer
            answer={surveyResponse.data?.data[0].survey_data[surveyQuestion.id]?.answer}
          />
        )}
      </Card>
    );
  }
  return null;
};

interface SurveyContentProps {
  survey: SurveyListObject;
  candidateUUID: CandidateDetail['uuid'];
}

const SurveyContent = ({ survey, candidateUUID }: SurveyContentProps) => {
  const surveyDetails = useAPIQuery([`/alchemer/survey/${survey.id}/`], {
    select(survey) {
      return survey.pages
        .map((surveyPage) => surveyPage.questions)
        .flatMap((questions) => questions, [])
        .filter(
          (surveyQuestion) =>
            ['RADIO', 'CHECKBOX', 'TEXTBOX', 'ESSAY'].includes(surveyQuestion.type) &&
            surveyQuestion.properties.map_key !== 'email'
        );
    },
  });

  return (
    <Spin spinning={surveyDetails.isLoading}>
      {surveyDetails.data && surveyDetails.data.length > 0 ? (
        <Space direction="vertical">
          {surveyDetails.data.map((surveyQuestion) => (
            <SurveyCard
              survey={survey}
              candidateUUID={candidateUUID}
              surveyQuestion={surveyQuestion}
            />
          ))}
        </Space>
      ) : (
        <Card />
      )}
    </Spin>
  );
};

interface SurveyResponseProps {
  candidateUUID: CandidateDetail['uuid'];
  survey: SurveyListObject;
}

const SurveyResponse = ({ candidateUUID, survey, ...props }: SurveyResponseProps) => {
  const surveyResponse = useAPIQuery([
    `/alchemer/survey/${survey.id}/surveyresponse/`,
    {
      params: {
        'filter[field][1]': '[url("sguid")]',
        'filter[operator][1]': '=',
        'filter[value][1]': String(candidateUUID),
      },
    },
  ]);

  if (surveyResponse.data && surveyResponse.data?.data.length > 0) {
    return (
      <Collapse.Panel
        {...{
          ...props,
          ...(surveyResponse.data.data[0].status === 'Complete' //hack to disallow interaction with ongoing survey response (https://github.com/react-component/collapse/blob/c723d76fe13b1c2bf658c1d7775ed3b3a7026043/src/Panel.tsx#L23)
            ? {}
            : {
                onItemClick: () => {
                  return null;
                },
              }),
        }}
        key={survey.id}
        header={<SurveyHeader survey={survey} surveyResponse={surveyResponse.data.data[0]} />}
        extra={<QuizScore surveyResponse={surveyResponse.data.data[0]} />}
        className={styles.collapsePanel}
        collapsible={surveyResponse.data.data[0].status === 'Complete' ? undefined : 'disabled'}
      >
        {surveyResponse.data.data[0].status === 'Complete' && (
          <SurveyContent survey={survey} candidateUUID={candidateUUID} />
        )}
      </Collapse.Panel>
    );
  } else {
    return null;
  }
};

const CandidateCustomTests = () => {
  const { applicant_id } = useTypedParams({
    tenant: String,
    id: Optional(JobPostingId),
    applicant_id: CandidateId,
  });
  const api = useAPIClient();

  const candidate = useAPIQuery(`/candidates/${applicant_id}/`);

  const surveys = useAPIQuery(['/alchemer/survey/'], {
    refetchOnWindowFocus: false,
  });

  const urlsSurveyResponse = surveys.data?.data.map(
    (svo) => `/alchemer/survey/${svo.id}/surveyresponse/`
  );

  const surveyResponses = useQuery(['surveyResponses', applicant_id], {
    queryFn: async () => {
      const surveyResponses: SurveyResponseList[] = [];
      for (const surveyUrl of urlsSurveyResponse || []) {
        const surveyResponse = (
          await api.get(surveyUrl as `/alchemer/survey/${number}/surveyresponse/`, {
            params: {
              'filter[field][1]': '[url("sguid")]',
              'filter[operator][1]': '=',
              'filter[value][1]': candidate.data?.uuid || '',
            },
          })
        ).data;

        if (surveyResponse.data) {
          if (surveyResponse.data.length > 0) {
            surveyResponses.push(surveyResponse.data[0]);
          }
        }
      }
      return surveyResponses;
    },
    enabled: !!surveys.data && urlsSurveyResponse && urlsSurveyResponse?.length > 0,
  });

  const isFetchingResponse = useIsFetching({
    predicate: (query) => {
      if (Array.isArray(query.queryKey)) {
        const url: string = query.queryKey[0];
        if (urlsSurveyResponse?.includes(url)) {
          return true;
        }
      }
      return false;
    },
  });

  return (
    <ErrorResult error={candidate.error}>
      <Helmet>
        <title>
          Testes customizados | {candidate.data?.name || '...'} | Recrutamento e Seleção
        </title>
      </Helmet>
      <Spin spinning={candidate.isLoading || surveys.isFetching || isFetchingResponse > 0}>
        {surveys.data &&
        (surveys.data.data || []).length > 0 &&
        surveyResponses.data &&
        surveyResponses.data?.length > 0 ? (
          <Collapse bordered={false} expandIconPosition="end" className={styles.collapse}>
            {surveys.data.data?.map((surveyListObject) => (
              <SurveyResponse
                candidateUUID={candidate.data?.uuid || ''}
                survey={surveyListObject}
              />
            ))}
          </Collapse>
        ) : (
          <Empty />
        )}
      </Spin>
    </ErrorResult>
  );
};

export default CandidateCustomTests;
